class SamplePicker extends HTMLElement {
	static stateBeforeCartRefresh = null;
	constructor() {
		super();
		this.handleChange = this.handleChange.bind(this);
		this.handleScroll = window.throttle(this.handleScroll.bind(this), 100);
		this.handleCartRefresh = this.handleCartRefresh.bind(this);
		this.cachedScrollLeft = 0;
	}

	connectedCallback() {
		this.config = window.samplePickerConfig || {};
		this.maxSelection = this.config.maxSelection || 2;

		this.samplesList = this.querySelector('.samples-list');
		this.pickerToggle = this.querySelector('.picker-toggle');
		this.collapsible = this.querySelector('.picker-content');
		if (!this.samplesList || !this.config.samples?.length) return;

		this.selectedIds = new Set();

		this.samplesList.addEventListener('change', this.handleChange);
		this.samplesList.addEventListener('scroll', this.handleScroll);
		// Capture phase ensures we save state before cart drawer processes refresh
		document.documentElement.addEventListener('cart:refresh', this.handleCartRefresh, true);
		this.syncWithCart();
		this.restoreState();
	}

	disconnectedCallback() {
		if (!this.samplesList) return;
		this.samplesList.removeEventListener('change', this.handleChange);
		this.samplesList.removeEventListener('scroll', this.handleScroll);
		document.documentElement.removeEventListener('cart:refresh', this.handleCartRefresh, true);
	}
	handleCartRefresh() {
		this.saveState();
	}

	handleScroll() {
		this.cachedScrollLeft = this.samplesList?.scrollLeft || 0;
	}

	saveState() {
		// toggle is custom element with isExpanded property
		const isExpanded = this.pickerToggle?.isExpanded;
		SamplePicker.stateBeforeCartRefresh = {
			isExpanded,
			scrollLeft: this.cachedScrollLeft,
			timestamp: Date.now(),
		};
	}

	restoreState() {
		const stateBeforeCartRefresh = SamplePicker.stateBeforeCartRefresh;
		if (!stateBeforeCartRefresh) return;

		if (Date.now() - stateBeforeCartRefresh.timestamp > 5000) {
			SamplePicker.stateBeforeCartRefresh = null;
			return;
		}

		if (stateBeforeCartRefresh.isExpanded) {
			this.pickerToggle?.setAttribute('aria-expanded', 'true');
			this.collapsible?.setAttribute('open', 'true');
		}
		if (stateBeforeCartRefresh.scrollLeft > 0 && this.samplesList) {
			requestAnimationFrame(() => {
				this.samplesList.scrollLeft = stateBeforeCartRefresh.scrollLeft;
				this.cachedScrollLeft = stateBeforeCartRefresh.scrollLeft;
			});
		}

		SamplePicker.stateBeforeCartRefresh = null;
	}

	async syncWithCart() {
		if (!this.config.samples?.length) return;

		const cartIds = await this.getCartVariantIds();
		const sampleIds = this.config.samples.map((s) => String(s.variantId));
		this.selectedIds = new Set(cartIds.filter((id) => sampleIds.includes(id)));

		this.updateCheckboxes();
	}

	async getCartVariantIds() {
		const cartItemsEl = document.querySelector('.cart__items');

		if (cartItemsEl?.dataset.cartVariants) {
			try {
				return JSON.parse(cartItemsEl.dataset.cartVariants).map(String);
			} catch (e) {
				console.warn('SamplePicker: failed to parse data-cart-variants', e);
				return [];
			}
		}
		try {
			const res = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
			if (!res.ok) return [];
			const cart = await res.json();
			return cart.items.map((item) => String(item.variant_id));
		} catch (e) {
			console.warn('SamplePicker: fetch fallback failed', e);
			return [];
		}
	}

	updateCheckboxes() {
		this.samplesList.querySelectorAll('.sample-item').forEach((item) => {
			const id = item.dataset.id;
			const checkbox = item.querySelector('.sample-item__checkbox');
			if (!id || !checkbox) return;

			const isSelected = this.selectedIds.has(id);
			checkbox.checked = isSelected;
			item.classList.toggle('is-selected', isSelected);
		});

		this.updateDisabledState();
	}

	handleChange(e) {
		const checkbox = e.target;
		if (!checkbox.classList.contains('sample-item__checkbox')) return;

		const item = checkbox.closest('.sample-item');
		const id = item?.dataset.id;
		if (!id) return;

		this.toggleSelection({ id, checked: checkbox.checked, item, checkbox });
	}

	handleFocusIn(e) {
		const checkbox = e.target.closest('.sample-item__checkbox');
		if (!checkbox || !this.samplesList.contains(checkbox)) return;

		const item = checkbox.closest('.sample-item');
		if (!item) return;

		item.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		});
	}

	toggleSelection({ id, checked, item, checkbox }) {
		const isSelected = this.selectedIds.has(id);

		if (checked && !isSelected) {
			if (this.selectedIds.size >= this.maxSelection) {
				checkbox.checked = false;
				return;
			}
			this.selectedIds.add(id);
			item.classList.add('is-selected');
		} else if (!checked && isSelected) {
			this.selectedIds.delete(id);
			item.classList.remove('is-selected');
		}

		this.updateDisabledState();
		this.emitChange();
	}

	updateDisabledState() {
		const maxReached = this.selectedIds.size >= this.maxSelection;

		this.samplesList.querySelectorAll('.sample-item').forEach((item) => {
			const id = item.dataset.id;
			const checkbox = item.querySelector('.sample-item__checkbox');
			if (!id || !checkbox) return;

			const isSelected = this.selectedIds.has(id);
			const shouldDisable = !isSelected && maxReached;
			checkbox.disabled = shouldDisable;
			item.classList.toggle('is-disabled', shouldDisable);
		});
	}

	emitChange() {
		this.dispatchEvent(
			new CustomEvent('sample-picker:change', {
				bubbles: true,
				detail: { selectedIds: Array.from(this.selectedIds) },
			}),
		);
	}
}

if (!customElements.get('sample-picker')) {
	customElements.define('sample-picker', SamplePicker);
}
